// Identity stays in the mapped resource; image availability never controls playback.
export const resourceKey = (kind, asset) => kind === "pwo"
  ? `pwo:${asset.source_pwo_id}:${asset.scene_name}`
  : `video:${asset.source_video_id}:${asset.tag_id ?? "full"}`;

export function readableMediaLabel(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  return raw
    .replace(/^ui[_\s-]+/i, "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\bmainmenu\b/gi, "Main Menu")
    .replace(/\bpendulummachinescene\b/gi, "Pendulum Machine Scene")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function resourceTileLabels(kind, asset) {
  const isPwo = kind === "pwo";
  const scene = readableMediaLabel(asset.scene_label || asset.scene_name);
  const tag = readableMediaLabel(asset.tag_name);
  const parent = readableMediaLabel(isPwo ? asset.pwo_name : asset.video_name);
  return {
    title: isPwo ? (scene || parent || "3D Exploration") : (tag || parent || "Video Lesson"),
    subtitle: isPwo && scene && parent && scene !== parent ? parent :
      !isPwo && tag && parent && tag !== parent ? parent : "",
  };
}

const validPreview = (record) => {
  if (!record || typeof record !== "object" || record.thumbnail_suppressed_reason || record.suppressed) return null;
  const image = record.image;
  if (typeof image !== "string" || !image.trim()) return null;
  // Preview catalog comes from the existing private authored-package API.
  // Allow unchanged data URIs/HTTPS URLs only. Never synthesize paths or borrow another ID.
  return /^(data:image\/(?:png|jpeg|webp);base64,|https:\/\/)/i.test(image) ? image : null;
};

export function getResourceTilePreview(kind, asset, catalog) {
  if (!catalog || !asset) return { uri: null, description: "" };
  const isPwo = kind === "pwo";
  const sourceId = String(isPwo ? asset.source_pwo_id : asset.source_video_id);
  const parent = isPwo
    ? catalog.pwo_by_source_id?.[sourceId]
    : catalog.video_by_source_id?.[sourceId];
  const denied = !!(parent?.thumbnail_suppressed_reason || parent?.suppressed);
  const detailId = isPwo ? String(asset.scene_name || "") : String(asset.tag_id ?? "full");
  // Future Content Factory exact-scene/tag lookups; intentionally require exact composite key.
  const exact = isPwo
    ? catalog.pwo_scene_by_key?.[`${sourceId}:${detailId}`]
    : catalog.video_tag_by_key?.[`${sourceId}:${detailId}`];
  // Preserve prior reviewed source-level previews. Do not use another source's poster.
  const uri = (denied || exact?.thumbnail_suppressed_reason || exact?.suppressed)
    ? null : (validPreview(exact) || validPreview(parent));
  const description = isPwo
    ? parent?.scenes?.[detailId.toLowerCase()] || ""
    : parent?.tag_descriptions?.[detailId] || parent?.video_description || "";
  return { uri, description: typeof description === "string" ? description : "" };
}
